import { useAppConfig } from "~/app/config/useAppConfig";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { Card } from "~/features/configurator/Linear/components/Card";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { useAddToCartForm } from "~/features/addToCart/hooks/useAddToCartForm";
import { makeSalesAwarePackCodeProvider } from "~/features/salesCampaign/utils/salesAwarePackCodeProvider";
import { useTranslation } from "react-i18next";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { Product } from "~/domains/product/Product";
import { SchaefflerIdParameter } from "~/domains/product/components/ProductParams";
import { ButtonBase, Collapse, IconButton, Stack, styled } from "~/common/mui";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "~/utils/constants";
import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { BasicAddToWishlistButton } from "~/domains/product/components/AddToWishlistButton";
import { AddToCartButtonBase } from "~/domains/product/components/AddToCartButton";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import { FCCTag } from "~/features/freeCapacityPush/components/FCCTag/FCCTag";
import { LinkButton } from "~/common/components/Link";
import { useState } from "react";
import Parameter from "~/common/components/Parameter";
import { getCanUserAddToCart } from "~/features/productDetails/components/ProductSummary/ExtendedPdpSummary";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { Price as PriceV3 } from "~/domains/pricing/components/Price/v3/Price";
import { produce } from "immer";
import { FccAction, trackFccAction } from "~/features/freeCapacityPush/api/trackFccAction";
import { Alert } from "~/common/mui/Alert/Alert";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";
import { addToCartItemFromSubmitData } from "~/features/addToCart/redux/addToCart/addToCartItemFactory";
import type { PriceReferenceSelection } from "~/domains/priceReference/types";

const CARD_PADDING = 24;

const RootCard = styled(Card)({});

const Content = styled("div")(({ theme }) => ({
  padding: theme.spacing(3),
  position: "relative",
  display: "grid",
  gridTemplateColumns: "auto auto 1fr",
  gap: theme.spacing(2),
}));

const SelectButton = styled(IconButton)(({ theme }) => ({
  alignSelf: "flex-start",
  margin: theme.spacing(-1.5),
}));

const DataSection = styled("div")(({ theme }) => ({
  display: "grid",
  columnGap: theme.spacing(4),
  rowGap: theme.spacing(2),
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  gridColumn: 3,
  gridRow: "1 / span 2",
}));

const Designation = styled("div")(({ theme }) => ({
  ...theme.typography.titleLarge,
  fontWeight: "bold",
  display: "flex",
  alignItems: "baseline",
}));

const NetPriceSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifySelf: "flex-end",
  alignSelf: "flex-start",
});

const ActionSection = styled("div")(({ theme }) => ({
  gridColumn: "1 / -1",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: theme.spacing(2),
}));

const Buttons = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
}));

const Packaging = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const TagSection = styled("div")(({ theme }) => ({
  position: "relative",
  left: -CARD_PADDING,
  gridColumn: "1 / 3",
  gridRow: 2,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  placeSelf: "flex-end flex-start",
}));

const PackagingContent = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2, 3),
  background: theme.palette.surface.main,
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: theme.spacing(2),
}));

const PackagingItem = styled(Card)(({ theme }) => ({
  background: theme.palette.common.white,
  padding: theme.spacing(2),
  display: "flex",
  gap: theme.spacing(2),
  alignItems: "unset",
  justifyContent: "unset",
}));

const PriceText = styled("span", {
  shouldForwardProp: (prop) => prop !== "mainPrice" && prop !== "priceOnRequest",
})<{ mainPrice?: boolean; priceOnRequest?: boolean }>(({ theme, mainPrice, priceOnRequest }) => ({
  textAlign: "right",
  ...theme.typography.titleLarge,
  ...(mainPrice && {
    ...theme.typography.headlineMedium,
    fontWeight: "bold",
  }),
  ...(priceOnRequest && {
    ...theme.typography.titleMedium,
  }),
}));

const MainPrice = styled(PriceV3)(({ theme }) => ({
  textAlign: "right",
  ...theme.typography.headlineMedium,
  fontWeight: "bold",
}));

const PriceLabels = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

interface Props {
  className?: string;
  productCode: string;
  isSelected?: boolean;
  onSelect?: (productCode: string | null, fifteenDigits: string | null) => void;
  searchQuery: string;
}

export const SearchResultFCC = (props: Props) => {
  const { selection, status } = usePriceReferenceSelection();

  if (status !== "ready") {
    return null;
  }

  return <ReadySearchResultFCC {...props} priceReferenceSelection={selection} />;
};

const ReadySearchResultFCC = ({
  className,
  productCode,
  searchQuery,
  onSelect: onSelectExternal,
  isSelected,
  priceReferenceSelection,
}: Props & { priceReferenceSelection: PriceReferenceSelection | undefined }) => {
  const { t } = useTranslation();
  const [packageSelectionOpen, setPackageSelectionOpen] = useState(false);

  const { form, productDescriptor, main, prepareSubmitData } = useAddToCartForm({
    productCode,
    priceReferenceSelection,
    // The FCC search payload owns isFcc/preselect metadata used to resolve the package.
    fetchProductData: false,
    // initialPackCodeProvider: makeSalesAwarePackCodeProvider(null, queryBasedPackCodeProvider(searchQuery)),
    initialPackCodeProvider: (productDescriptor, quantity) => {
      const alteredProductDescriptor = produce(productDescriptor, (draft) => {
        const fccPackages = getFccPackages(productDescriptor);
        if (draft.availability) {
          draft.availability.packagingInformation = fccPackages;
        }
      });

      const preselectEntries = Object.entries(productDescriptor.base.fifteenDigit)
        .filter(([packCode, entry]) => entry.preselect)
        .map(([packCode, _]) => packCode);
      const preselectedPackageEntry = alteredProductDescriptor.availability?.packagingInformation?.filter((pack) =>
        preselectEntries.includes(pack.packCode),
      )[0];
      if (preselectedPackageEntry) {
        return preselectedPackageEntry.packCode;
      }

      return makeSalesAwarePackCodeProvider(null)(alteredProductDescriptor, quantity);
    },
  });

  const selectedPack = main.selectedPack;
  const packSize = main.selectedPackaging?.packSize ?? selectedPack?.packSize ?? 1;
  const onSelectPackage = (packCode: string) => {
    form.setValue("selectedPackCode", packCode);
    const fifteenDigits = productDescriptor.base.sapMaterialNumber + packCode;
    onSelectExternal?.(productCode, fifteenDigits);
  };
  const packCode = form.watch("selectedPackCode");

  const quantity = form.watch("quantity");

  let isPackageSizeInvalid = false;
  if (selectedPack) {
    isPackageSizeInvalid = quantity === 0 || quantity % packSize > 0;
  }

  const priceReference = form.watch("priceReference");

  const addToWishlistAvailable = canSeeWishlistButton();
  const addToCartAvailable = canSeeAddToCartButton();

  const getPackagingLabel = (packCode: string, packSize: number) => {
    return t("addToCartPopup.packSize", {
      amount: packSize,
      code: packCode,
    });
  };

  const wishlistWizardProps = {
    productInput: productDescriptor.base,
    initialQuantity: form.watch("quantity"),
    initialPriceReference: form.watch("priceReference"),
  };

  const userCanAddToCart = getCanUserAddToCart(productDescriptor, selectedPack);

  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;
  const addToCartDisabled =
    webservicesDisabled ||
    !userCanAddToCart ||
    isPackageSizeInvalid ||
    productDescriptor.isLoading ||
    productDescriptor.isError;

  const doAddToCart = () => {
    const submitData = prepareSubmitData();
    void addToCart({
      items: [
        addToCartItemFromSubmitData({
          submitData,
          productCode,
          restrictionQuery: "",
          context: { page: "FCC" },
        }),
      ],
      page: "FCC",
      source: "fcc-addToCart",
    }).catch(() => undefined);
  };

  const price = main.netPrice;
  const regularPriceValid = price && price.value > 0;

  const fifteenDigits = productDescriptor.base.sapMaterialNumber + packCode;

  const onSelect = (newValue: boolean) => {
    if (newValue) {
      trackFccAction({
        actionType: FccAction.SelectProduct,
        actionContent: fifteenDigits,
      });
    }
    onSelectExternal?.(newValue ? productCode : null, newValue ? fifteenDigits : null);
  };

  const fccPackages = getFccPackages(productDescriptor);
  const hasMultiplePackages = fccPackages.length > 1;

  return (
    <ProductDescriptorContext value={productDescriptor}>
      <RootCard className={className} data-test-id={`search-result-item`} active={isSelected}>
        <Content>
          <SelectButton
            onClick={() => onSelect(!isSelected)}
            disabled={productDescriptor.isLoading || productDescriptor.isError}
          >
            <CheckedCircle checked={isSelected} alwaysVisible={true} />
          </SelectButton>
          <Product.PrimaryImageWithLogos linkProps={{ target: "_blank" }} />
          <DataSection>
            <Stack gap={2}>
              <Stack gap={1}>
                <Designation>
                  {productDescriptor.base.preferred && <Product.PreferredIndicator className={"icon-margin"} />}
                  <Product.Designation linkProps={{ target: "_blank" }} />
                </Designation>
                <Product.ShortDescription clickable={false} />
              </Stack>
              <SchaefflerIdParameter variant={"horizontal"} />
            </Stack>
            <NetPriceSection>
              {productDescriptor.isLoading && (
                <div className={"mb-30"}>
                  <CircleSpinner />
                </div>
              )}
              {productDescriptor.isLoaded && (
                <>
                  {!regularPriceValid && true && (
                    <PriceText priceOnRequest mainPrice>
                      {t("product.priceOnRequest")}
                    </PriceText>
                  )}
                  {regularPriceValid && <MainPrice data-test-id={"fcc-net-price"} price={price} />}
                  {(regularPriceValid || false) && (
                    <PriceLabels>
                      <PriceV3.NetPriceUnit price={price} surchargeEnabled={false} />
                    </PriceLabels>
                  )}
                </>
              )}
            </NetPriceSection>
            <QuantityInput
              min={packSize}
              max={MAX_QUANTITY}
              value={quantity}
              disabled={productDescriptor.isLoading || !productDescriptor.base.quantityVisible}
              step={packSize}
              data-test-id={"quantity-input"}
              onValueChange={(value) => {
                form.setValue("quantity", value, { shouldTouch: true, shouldDirty: true });
                onSelectExternal?.(productCode, fifteenDigits);
              }}
              error={isPackageSizeInvalid}
            />
            <PriceReferenceParam
              product={productDescriptor.base}
              priceReference={priceReference}
              data-test-id={"price-reference"}
              onChange={(value) => {
                form.setValue("priceReference", value ?? "");
                onSelectExternal?.(productCode, fifteenDigits);
              }}
              editable={canSeePriceRefColumn(priceReferenceSelection)}
            />
            <ActionSection>
              <Packaging>
                {productDescriptor.isLoading && <CircleSpinner />}
                {productDescriptor.isLoaded && (
                  <>
                    <span data-test-id={"fcc-packaging"}>{getPackagingLabel(packCode, packSize)}</span>
                    {hasMultiplePackages && (
                      <LinkButton
                        onClick={() => setPackageSelectionOpen((prev) => !prev)}
                        iconBefore={packageSelectionOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      >
                        {t("page.fcc.product.changePackage")}
                      </LinkButton>
                    )}
                  </>
                )}
                {productDescriptor.isError && <Alert severity={"error"}>{t("hybrisAjaxError")}</Alert>}
              </Packaging>
              <Buttons>
                {addToWishlistAvailable && (
                  <BasicAddToWishlistButton
                    size={"medium"}
                    sideButton={true}
                    color={"secondary"}
                    wizardProps={wishlistWizardProps}
                    onClick={() => {
                      trackFccAction({
                        actionType: FccAction.AddToWishlist,
                        actionContent: fifteenDigits,
                        netPrice: main.netPrice?.value,
                        quantity: form.getValues().quantity,
                        priceUnit: main.netPrice?.unit ?? 1,
                      });
                    }}
                  />
                )}
                {addToCartAvailable && (
                  <AddToCartButtonBase
                    size={"medium"}
                    actionVariant={"salesman"}
                    disabled={addToCartDisabled}
                    onClick={doAddToCart}
                  />
                )}
              </Buttons>
            </ActionSection>
          </DataSection>
          <TagSection>
            <FCCTag anchor={"left"} />
          </TagSection>
        </Content>
        <Collapse in={packageSelectionOpen}>
          <PackagingContent>
            {fccPackages.map((packaging) => (
              <PackagingItem
                key={packaging.packCode}
                as={ButtonBase}
                onClick={() => onSelectPackage(packaging.packCode)}
              >
                <CheckedCircle checked={packCode === packaging.packCode} alwaysVisible={true} />
                <Parameter
                  label={t("availabilityTable.packagingCode")}
                  value={getPackagingLabel(packaging.packCode, packaging.packSize)}
                />
              </PackagingItem>
            ))}
          </PackagingContent>
        </Collapse>
      </RootCard>
    </ProductDescriptorContext>
  );
};

function getFccPackages(productDescriptor: ProductDescriptor) {
  const packages = productDescriptor.availability?.packagingInformation;
  if (packages) {
    const fccPackageCodes = Object.entries(productDescriptor.base.fifteenDigit)
      .filter(([_, entry]) => entry.isFcc)
      .map(([packCode, _]) => packCode);

    return packages.filter((pack) => fccPackageCodes.includes(pack.packCode));
  }

  return [];
}

AnalyticsMQ.instance.subscribe("addToCart", ({ products, context: { source } }) => {
  if (source !== "fcc-addToCart") {
    return;
  }

  (Array.isArray(products) ? products : [products]).forEach(({ product, packaging, quantity, price }) => {
    const fifteenDigits = product.sapMaterialNumber + packaging.code;
    trackFccAction({
      actionType: FccAction.AddToQuote,
      actionContent: fifteenDigits,
      netPrice: price?.value,
      quantity: quantity,
      priceUnit: price?.unit ?? 1,
    });
  });
});
